// crewbit-admin/src/lib/vendorBillMutations.ts
//
// Phase B.3.4 — Money Module RPC-routed vendor bill mutations (admin / operator).
// Spec: docs/references/MONEY_MODULE_DATA_v1.md §5.8 (create/update); §5.14 (record_expense_payment).
// Mirrors crewbit-mobile/lib/vendorBillMutations.ts (B.3.3, commit 9d93b0b).
//
// §Money.StubActionSweepCompletion Phase 3 — `createVendor` wrapper added below
// the bill mutations; consumed by the HeaderQuickActions add-vendor verb +
// inline-add affordance from the vendor-invoice modal. Migration 291.
//
// Plain async functions, not a hook — they don't read or write React state.
// Identity (created_by, business_id) resolves server-side via
// get_public_user_id() / get_user_business_id() inside the RPC bodies; the
// client never passes them.
//
// `markPaid` calls `record_expense_payment` (Session 99 migration 229) which —
// when `expenseCategory` is non-NULL — atomically performs four writes in one
// transaction: vendor_bills status update + payment_ledger row + expense row +
// vendor_bills.expense_id link. Replaces the legacy client-side three/four-step
// markPaid block (admin useOutstanding markVendorBillPaid).

import { callMoneyRpc, type MoneyRpcResult } from './moneyRpc';

export interface CreateVendorBillArgs {
  businessId: string;
  vendorName: string;
  amount: number;
  /** REQUIRED — caller generates at form-load time via `useState(() => uuidv4())`. */
  idempotencyKey: string;
  /** ISO date 'YYYY-MM-DD'. */
  billDate?: string | null;
  dueDate?: string | null;
  description?: string | null;
  documentUrl?: string | null;
  /** FK to vendors row when one exists; null for free-form vendor names. */
  vendorId?: string | null;
  hstAmount?: number | null;
  projectId?: string | null;
}

export type CreateVendorBillResult = MoneyRpcResult<{
  vendor_bill_id: string;
  already_created: boolean;
}>;

export async function createVendorBill(args: CreateVendorBillArgs): Promise<CreateVendorBillResult> {
  return callMoneyRpc('create_vendor_bill', {
    p_business_id: args.businessId,
    p_vendor_name: args.vendorName,
    p_amount: args.amount,
    p_bill_date: args.billDate,
    p_due_date: args.dueDate,
    p_description: args.description,
    p_document_url: args.documentUrl,
    p_idempotency_key: args.idempotencyKey,
    p_vendor_id: args.vendorId,
    p_hst_amount: args.hstAmount,
    p_project_id: args.projectId,
  });
}

export interface UpdateVendorBillArgs {
  vendorBillId: string;
  vendorName?: string | null;
  vendorId?: string | null;
  amount?: number | null;
  hstAmount?: number | null;
  billDate?: string | null;
  dueDate?: string | null;
  description?: string | null;
  documentUrl?: string | null;
  projectId?: string | null;
  archived?: boolean | null;
}

export type UpdateVendorBillResult = MoneyRpcResult<{
  vendor_bill_id: string;
  updated_fields: string[];
}>;

export async function updateVendorBill(args: UpdateVendorBillArgs): Promise<UpdateVendorBillResult> {
  return callMoneyRpc('update_vendor_bill', {
    p_vendor_bill_id: args.vendorBillId,
    p_vendor_name: args.vendorName,
    p_vendor_id: args.vendorId,
    p_amount: args.amount,
    p_hst_amount: args.hstAmount,
    p_bill_date: args.billDate,
    p_due_date: args.dueDate,
    p_description: args.description,
    p_document_url: args.documentUrl,
    p_project_id: args.projectId,
    p_archived: args.archived,
  });
}

/** Thin wrapper of `updateVendorBill` — sets archived flag. Status guard: paid/cancelled bills allow archive only. */
export async function archiveVendorBill(vendorBillId: string): Promise<UpdateVendorBillResult> {
  return updateVendorBill({ vendorBillId, archived: true });
}

export interface MarkPaidArgs {
  vendorBillId: string;
  amount: number;
  paymentMethod: string;
  /** ISO date 'YYYY-MM-DD'. Defaults server-side to current_date when null. */
  paymentDate?: string | null;
  /** REQUIRED — caller generates at form-load time. */
  idempotencyKey: string;
  /**
   * When non-NULL, RPC atomically creates an expense row in this category and
   * links it to the vendor_bill via expense_id. NULL means "mark paid only,
   * no expense linkage" (rare — most bills represent business expenses).
   */
  expenseCategory?: string | null;
}

export type MarkPaidResult = MoneyRpcResult<{
  vendor_bill_id: string;
  ledger_id: string;
  expense_id: string | null;
  already_paid: boolean;
}>;

export async function markVendorBillPaid(args: MarkPaidArgs): Promise<MarkPaidResult> {
  return callMoneyRpc('record_expense_payment', {
    p_vendor_bill_id: args.vendorBillId,
    p_amount: args.amount,
    p_payment_method: args.paymentMethod,
    p_payment_date: args.paymentDate,
    p_idempotency_key: args.idempotencyKey,
    p_expense_category: args.expenseCategory,
  });
}

export interface CreateVendorArgs {
  businessId: string;
  companyName: string;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  serviceType?: string | null;
  paymentTerms?: 'net_15' | 'net_30' | 'net_60' | 'on_receipt';
  businessNumber?: string | null;
  /** Optional. Server uses company_name dedup within business when present. */
  idempotencyKey?: string | null;
}

export type CreateVendorResult = MoneyRpcResult<{
  vendor_id: string;
  idempotent_replay: boolean;
}>;

export async function createVendor(args: CreateVendorArgs): Promise<CreateVendorResult> {
  return callMoneyRpc('create_vendor', {
    p_business_id: args.businessId,
    p_company_name: args.companyName,
    p_contact_name: args.contactName ?? null,
    p_email: args.email ?? null,
    p_phone: args.phone ?? null,
    p_address: args.address ?? null,
    p_service_type: args.serviceType ?? null,
    p_payment_terms: args.paymentTerms ?? 'net_30',
    p_business_number: args.businessNumber ?? null,
    p_idempotency_key: args.idempotencyKey ?? null,
  });
}

// Wave 7 Step 4 — update_vendor wrapper (mirrors mobile lib/vendorMutations.ts).
// Partial-update: NULL preserves; '' clears nullable text col; value sets.
export interface UpdateVendorArgs {
  vendorId: string;
  companyName?: string | null;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  serviceType?: string | null;
  paymentTerms?: 'net_15' | 'net_30' | 'net_60' | 'on_receipt' | null;
  businessNumber?: string | null;
  defaultTaxType?: string | null;
  notes?: string | null;
  status?: 'active' | 'inactive' | null;
}

export type UpdateVendorResult = MoneyRpcResult<{ vendor_id: string }>;

export async function updateVendor(args: UpdateVendorArgs): Promise<UpdateVendorResult> {
  return callMoneyRpc('update_vendor', {
    p_vendor_id: args.vendorId,
    p_company_name: args.companyName,
    p_contact_name: args.contactName,
    p_email: args.email,
    p_phone: args.phone,
    p_address: args.address,
    p_service_type: args.serviceType,
    p_payment_terms: args.paymentTerms,
    p_business_number: args.businessNumber,
    p_default_tax_type: args.defaultTaxType,
    p_notes: args.notes,
    p_status: args.status,
  });
}

/** Thin wrapper over `updateVendor` — sets status='inactive'. */
export async function archiveVendor(vendorId: string): Promise<UpdateVendorResult> {
  return updateVendor({ vendorId, status: 'inactive' });
}
